import React, { useCallback } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { useLocalizedField } from '@/common/hooks/index';
import { Notification } from '@/common/utils/index';
import { FinInstitutionsForm } from '@/common/containers/Forms/index';
import { useTranslation } from 'react-i18next';
import { useSubmitAnswersMutation } from '@/entities/question';
import styles from '@/common/containers/Modals/FinAssetsModal/FinAssetsModal.styles';

const FIN_ASSET_FORM_ID = 'finAsset';

const FinAssetsModal = ({ form, onClose, open, refreshTask }) => {
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const decedentId = useCurrentDecedentId();
  const { question, answers, taskStepId } = form || {};
  const submitAnswersMutation = useSubmitAnswersMutation();
  const loading = submitAnswersMutation.isPending;

  const onSubmit = useCallback(
    async (form) => {
      if (!form.length) {
        Notification.info(t('No item added. Please add entry'));
        return;
      }
      const payload = form.map((ans) => ({ ...ans, taskStepId }));
      try {
        await submitAnswersMutation.mutateAsync({
          answers: payload,
          decedentId,
          section: 'main',
          sectionType: 'Task',
        });

        Notification.success(
          t('{{item}} is saved', {
            item: getLocalizedField(question, 'listItemName'),
          })
        );
      } catch (e) {
        Notification.error(
          t('{{item}} was not saved', {
            item: getLocalizedField(question, 'listItemName'),
          })
        );
      }
      onClose?.();
      await refreshTask?.();
    },
    [
      decedentId,
      getLocalizedField,
      onClose,
      question,
      refreshTask,
      submitAnswersMutation,
      taskStepId,
      t,
    ]
  );

  const closeModal = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick' || loading) {
        return;
      }

      onClose?.();
    },
    [loading, onClose]
  );

  return (
    <Dialog open={!!open} onClose={closeModal} fullWidth>
      <DialogTitle>
        {t('Add {{item}}', {
          item: getLocalizedField(question, 'listItemName'),
        })}
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <FinInstitutionsForm
          question={question}
          answers={answers}
          formId={FIN_ASSET_FORM_ID}
          onSubmit={onSubmit}
        />
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          variant="outlined"
          color="secondary"
          size="medium"
          onClick={onClose}
          disabled={loading}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="medium"
          form={FIN_ASSET_FORM_ID}
          loading={loading}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinAssetsModal;
